if (!customElements.get('variant-label')) {
  class VariantLabel extends HTMLElement {
    constructor() {
      super();
      this.variantLabels = this.querySelectorAll('.js-variant-label');
      this.variantPicker = null;
      this.variantMetafields = null;
      window.initLazyScript(this, this.initLazySection.bind(this));
    }

    initLazySection() {
      if (this.variantLabels) {
        // Get variant picker
        this.variantPicker = this.closest('.product').querySelector('variant-picker');
        if (this.variantPicker) {
          this.variantPicker.addEventListener('on:variant:change', this.handleVariantChange.bind(this));
        }

        // CRITICAL: Check URL for variant parameter first (takes precedence over data attribute)
        // This ensures correct variant label shows when clicking from collection.deals
        const urlParams = new URLSearchParams(window.location.search);
        const urlVariantId = urlParams.get('variant');
        
        // Use URL variant if present, otherwise fall back to data attribute
        const initialVariantId = urlVariantId || this.dataset.currentVariantId;
        
        if (initialVariantId) {
          // If URL variant exists, wait a bit for variant-picker to initialize
          // This ensures variant-picker data is available for updateCustomVariantLabel
          if (urlVariantId) {
            // Wait for variant-picker to be ready and have data
            this.waitForVariantPickerAndUpdate(urlVariantId);
          } else {
            // No URL variant, use immediate update
            this.setVisibleVariantLabel(initialVariantId);
          }
        }
      }
    }

    /**
     * Waits for variant-picker to be ready and then updates the variant label
     * This ensures the variant label updates correctly when coming from URL with variant parameter
     * @param {string} variantId - The variant ID to show
     */
    waitForVariantPickerAndUpdate(variantId) {
      const maxAttempts = 10;
      let attempts = 0;
      
      const tryUpdate = () => {
        attempts++;
        
        // Check if variant-picker is ready and has data
        if (this.variantPicker && this.variantPicker.data && this.variantPicker.data.formatted) {
          // Variant-picker is ready, update the label
          this.setVisibleVariantLabel(variantId);
        } else if (attempts < maxAttempts) {
          // Variant-picker not ready yet, try again after a short delay
          setTimeout(tryUpdate, 50);
        } else {
          // Fallback: update anyway even if variant-picker data isn't ready
          // The label will update when variant-picker fires the change event
          this.setVisibleVariantLabel(variantId);
        }
      };
      
      // Start trying immediately, then retry if needed
      tryUpdate();
    }

    /**
     * Sets the visible variant variantLabels.
     * @param {string} variantId - The variant id
     */
    setVisibleVariantLabel(variantId) {
      this.variantLabels.forEach((variantLabel) => {
        variantLabel.hidden = variantLabel.dataset.variantId.toString() !== variantId.toString();
      });

      // Update custom variant label if it exists
      this.updateCustomVariantLabel(variantId);
    }

    /**
     * Updates the custom variant label based on variant metafields
     * @param {string} variantId - The variant id
     */
    updateCustomVariantLabel(variantId) {
      if (!this.variantPicker || !variantId) return;

      // Get the variant data from the variant picker
      const variantData = this.variantPicker.data;
      if (!variantData || !variantData.formatted || !variantData.formatted[variantId]) return;

      const variantMetafields = variantData.formatted[variantId].metafields;
      const customVariantLabel = variantMetafields && variantMetafields.custom && variantMetafields.custom.variantlabel;

      // Look for existing custom variant label element
      let customLabelElement = this.querySelector('.product-label--variant-custom');
      
      if (customVariantLabel) {
        // Create or update custom variant label
        if (!customLabelElement) {
          customLabelElement = this.createCustomVariantLabel();
        }
        
        // Update the label text directly (since createCustomVariantLabel now returns the label element)
        customLabelElement.textContent = customVariantLabel;
        
        // Re-add icon if it was removed
        const iconElement = this.querySelector('.product-label--custom .icon');
        if (iconElement && !customLabelElement.querySelector('.icon')) {
          const iconClone = iconElement.cloneNode(true);
          customLabelElement.insertBefore(iconClone, customLabelElement.firstChild);
        }
        
        customLabelElement.hidden = false;
      } else if (customLabelElement) {
        // Hide the custom label if no metafield data
        customLabelElement.hidden = true;
      }
    }

    /**
     * Creates a custom variant label element
     * @returns {HTMLElement} The created label element
     */
    createCustomVariantLabel() {
      // Find existing product label wrapper to insert the variant label into
      const existingLabelWrapper = this.querySelector('.product-label-wrapper');
      
      if (existingLabelWrapper) {
        // Insert variant label into existing wrapper
        const label = document.createElement('span');
        label.className = 'product-label product-label--variant-custom';
        
        // Add icon if settings specify one
        const iconElement = this.querySelector('.product-label--custom .icon');
        if (iconElement) {
          const iconClone = iconElement.cloneNode(true);
          label.appendChild(iconClone);
        }
        
        existingLabelWrapper.appendChild(label);
        return label;
      } else {
        // Fallback: create new wrapper if no existing wrapper found
        const labelWrapper = document.createElement('div');
        labelWrapper.className = 'product-label-wrapper product-label-wrapper--variant-custom flex';
        
        const label = document.createElement('span');
        label.className = 'product-label product-label--variant-custom';
        
        // Add icon if settings specify one
        const iconElement = this.querySelector('.product-label--custom .icon');
        if (iconElement) {
          const iconClone = iconElement.cloneNode(true);
          label.appendChild(iconClone);
        }
        
        labelWrapper.appendChild(label);
        this.appendChild(labelWrapper);
        
        return label;
      }
    }

    /**
     * Handles a 'change' event on the variant picker element
     * @param {object} evt - Event object
     */
    handleVariantChange(evt) {
      if (evt.detail.variant) {
        this.setVisibleVariantLabel(evt.detail.variant.id);
      }
    }
  }

  customElements.define('variant-label', VariantLabel);
}
